import { getCodigoTelPorPais } from '../utils/constants';

export function getCountryCode(pais: string): string {
  return getCodigoTelPorPais(pais);
}

export type RegisterWhatsappIso = 'CO' | 'MX' | 'AR' | 'VE' | 'HN' | 'CL' | 'PE' | 'US';

export const REGISTER_WHATSAPP_COUNTRIES: Array<{
  iso: RegisterWhatsappIso;
  dial: string;
  flag: string;
  label: string;
  localMax: number;
  placeholder: string;
  hint: string;
  pattern: RegExp;
}> = [
  {
    iso: 'CO',
    dial: '57',
    flag: '🇨🇴',
    label: 'Colombia',
    localMax: 10,
    placeholder: '3XX XXX XXXX',
    hint: '10 dígitos, empieza con 3',
    pattern: /^3\d{9}$/,
  },
  {
    iso: 'MX',
    dial: '52',
    flag: '🇲🇽',
    label: 'México',
    localMax: 10,
    placeholder: '55 1234 5678',
    hint: '10 dígitos',
    pattern: /^\d{10}$/,
  },
  {
    iso: 'AR',
    dial: '54',
    flag: '🇦🇷',
    label: 'Argentina',
    localMax: 11,
    placeholder: '11 1234 5678',
    hint: '10 u 11 dígitos, sin 0 ni 15 al inicio',
    pattern: /^\d{10,11}$/,
  },
  {
    iso: 'VE',
    dial: '58',
    flag: '🇻🇪',
    label: 'Venezuela',
    localMax: 10,
    placeholder: '412 123 4567',
    hint: '10 dígitos, empieza con 4',
    pattern: /^4\d{9}$/,
  },
  {
    iso: 'HN',
    dial: '504',
    flag: '🇭🇳',
    label: 'Honduras',
    localMax: 8,
    placeholder: '9XXX XXXX',
    hint: '8 dígitos',
    pattern: /^\d{8}$/,
  },
  {
    iso: 'CL',
    dial: '56',
    flag: '🇨🇱',
    label: 'Chile',
    localMax: 9,
    placeholder: '9 1234 5678',
    hint: '9 dígitos, empieza con 9',
    pattern: /^9\d{8}$/,
  },
  {
    iso: 'PE',
    dial: '51',
    flag: '🇵🇪',
    label: 'Perú',
    localMax: 9,
    placeholder: '9XX XXX XXX',
    hint: '9 dígitos, empieza con 9',
    pattern: /^9\d{8}$/,
  },
  {
    iso: 'US',
    dial: '1',
    flag: '🇺🇸',
    label: 'Estados Unidos',
    localMax: 10,
    placeholder: '555 123 4567',
    hint: '10 dígitos',
    pattern: /^\d{10}$/,
  },
];

export function validarWhatsappRegistro(iso: RegisterWhatsappIso, localDigits: string): string | true {
  const c = REGISTER_WHATSAPP_COUNTRIES.find((x) => x.iso === iso);
  if (!c) return 'Selecciona un país';
  if (!c.pattern.test(localDigits)) return `Ingresa un celular válido (${c.hint})`;
  return true;
}

export function e164WhatsappRegistro(iso: RegisterWhatsappIso, localDigits: string): string {
  const c = REGISTER_WHATSAPP_COUNTRIES.find((x) => x.iso === iso) ?? REGISTER_WHATSAPP_COUNTRIES[0]!;
  return `+${c.dial}${localDigits.replace(/\D/g, '')}`;
}

export function formatWhatsAppPhone(jidDigits: string, empresaPais: string): string {
  const code = getCountryCode(empresaPais);
  const digits = jidDigits.replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.startsWith(code)) {
    const local = digits.slice(code.length);
    if (code === '57' && local.length === 10) {
      return local.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
    }
    return `+${code} ${local}`;
  }
  // 10 digits without country: treat as local mobile for the empresa pais
  if (code === '57' && digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
  }
  // Unprefixed long strings are often LID / internal bridge ids — not a dialable MSISDN.
  if (code === '57' && digits.length > 12) {
    return '';
  }
  if (digits.length > 15) {
    return '';
  }
  return `+${digits}`;
}

export function normalizePhoneForMatch(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Digits to prefill in forms — omit LID / pseudo-MSISDN (bridge sometimes sends 14+ digits without 57).
 */
export function displayOrEmptyClientePhoneFromWhatsapp(phone: string, jid: string | undefined, empresaPais: string): string {
  const d = phone.replace(/\D/g, '');
  if (!d) return '';
  if (jid?.toLowerCase().endsWith('@lid')) return '';
  const code = getCountryCode(empresaPais);
  if (code === '57') {
    if (d.length === 10) return d;
    if (d.length === 12 && d.startsWith('57')) return d;
    if (d.length > 12) return '';
  } else if (d.length > 15) {
    return '';
  }
  return d;
}

/**
 * Guardar `clientes.telefono` en E.164 sin +: dígitos con prefijo de país.
 * 10 dígitos locales (móvil CO que empieza en 3) se prefijan con el país de la empresa.
 * No anteponer el país de la tienda a un 10-dígitos extranjero (p. ej. US 555…).
 */
export function normalizeClientePhone(telefono: string, empresaPais: string): string {
  if (!telefono) return telefono;
  const digits = telefono.replace(/\D/g, '');
  if (!digits) return telefono;
  const code = getCountryCode(empresaPais);
  if (digits.startsWith(code) && digits.length > 10) return digits;
  if (digits.length === 10) {
    if (code === '57' && !digits.startsWith('3')) return digits;
    return code + digits;
  }
  return digits;
}

/** Prefer the live DOM value so iOS Contact AutoFill is saved even if React state lagged. */
export function telefonoDesdeFormulario(form: HTMLFormElement, fallback = ''): string {
  const el = form.elements.namedItem('telefono');
  if (el instanceof HTMLInputElement) return el.value;
  return fallback;
}
